import { useCopyToClipboard } from '@base/hooks/use-copy-to-clipboard';
import { CopyIcon, TickIcon } from '@icons';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import hljs from 'highlight.js';
import 'highlight.js/styles/an-old-hope.css';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { Marked } from 'marked';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Mermaid ships ESM-only and breaks Jest's transformer; lazy-import it so it
// only loads in the browser when a mermaid block is actually rendered.
type MermaidModule = typeof import('mermaid').default;
let mermaidPromise: Promise<MermaidModule> | null = null;
const getMermaid = (): Promise<MermaidModule> => {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((mod) => {
      const mermaid = mod.default;
      mermaid.initialize({
        startOnLoad: false,
        theme: 'dark',
        securityLevel: 'strict'
      });
      return mermaid;
    });
  }
  return mermaidPromise;
};

// Mermaid v11 appends a temporary host (and on failure a bomb-icon SVG) to
// document.body. Provide our own off-screen host so it never escapes our
// control, and on top of that sweep any stray nodes by id to be safe.
const cleanupMermaidArtifacts = (id: string, host: HTMLElement | null) => {
  if (typeof document === 'undefined') return;
  host?.remove();
  document.getElementById(id)?.remove();
  document.getElementById(`d${id}`)?.remove();
  document.getElementById(`i${id}`)?.remove();
};

function MermaidBlock({ code }: { code: string }) {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;
    const id = `mermaid-${Math.random().toString(36).substring(2, 11)}`;
    const host = document.createElement('div');
    host.style.cssText =
      'position:absolute;left:-9999px;top:0;width:0;height:0;visibility:hidden;overflow:hidden;';
    document.body.appendChild(host);

    (async () => {
      try {
        const mermaid = await getMermaid();
        const parseResult = await mermaid.parse(code, { suppressErrors: true });
        if (parseResult === false) {
          throw new Error('Invalid Mermaid syntax');
        }
        const result = await mermaid.render(id, code, host);
        if (!cancelled) {
          setError(null);
          setSvg(result.svg);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setSvg('');
        }
      } finally {
        cleanupMermaidArtifacts(id, host);
      }
    })();

    return () => {
      cancelled = true;
      cleanupMermaidArtifacts(id, host);
    };
  }, [code]);

  if (error) {
    return (
      <div className='my-sm flex flex-col gap-xs'>
        <pre
          className={classNames(
            'hljs P6R sm:P2R border-[2px] rounded-xs p-xs whitespace-pre-wrap break-words',
            setupForDark('border-secondary')
          )}
        >
          {code}
        </pre>
        <div
          role='alert'
          className='P6R sm:P4R border border-error text-error rounded-xs p-xs whitespace-pre-wrap break-words'
        >
          {`Mermaid error: ${error}`}
        </div>
      </div>
    );
  }

  return (
    <div
      className='mermaid-block w-full overflow-x-auto my-sm flex justify-center'
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

// Helper to decode HTML entities in the code
const decodeHtmlEntities = (text: string) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

function CopyButton({
  text,
  ariaLabel,
  show = true
}: {
  text: string;
  ariaLabel?: string;
  show?: boolean;
}) {
  const { copy } = useCopyToClipboard();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      const timeoutId = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [copied]);

  if (!show) return null;

  const handleCopy = async () => {
    await copy(text);
    setCopied(true);
  };

  return (
    <div
      onClick={handleCopy}
      role='button'
      aria-label={ariaLabel}
      className={`absolute top-xs right-xs bg-opacity-10 bg-white rounded-xxs p-xxs cursor-pointer flex items-center justify-center transition-all duration-200 z-10 ${
        copied ? 'bg-opacity-20 text-green-500 bg-green-500' : 'text-tertiary hover:bg-opacity-20'
      }`}
    >
      {copied ? <TickIcon size={20} /> : <CopyIcon size={20} />}
    </div>
  );
}

function CodeBlock({
  code,
  language,
  showCopyButton,
  compact = false,
  wrapCode = false
}: {
  code: string;
  language?: string;
  showCopyButton?: boolean;
  compact?: boolean;
  wrapCode?: boolean;
}) {
  const decodedCode = useMemo(() => decodeHtmlEntities(code), [code]);

  const { highlightedHtml, resolvedLanguage } = useMemo(() => {
    const normalizedLanguage = language?.trim().toLowerCase();
    const isLanguageSupported =
      normalizedLanguage && hljs.getLanguage(normalizedLanguage) !== undefined;
    const effectiveLanguage = isLanguageSupported ? normalizedLanguage : 'plaintext';
    const highlighted = hljs.highlight(decodedCode, { language: effectiveLanguage }).value;
    const sanitized = DOMPurify.sanitize(highlighted, {
      ALLOWED_TAGS: ['span', 'br'],
      ALLOWED_ATTR: ['class']
    });

    return { highlightedHtml: sanitized, resolvedLanguage: effectiveLanguage };
  }, [decodedCode, language]);

  const preClass = compact
    ? 'hljs overflow-x-auto P7R min-w-0 w-full max-w-full'
    : 'hljs overflow-x-auto P6R @sm:P2R min-w-0 w-full max-w-full';

  return (
    <div
      className={classNames(
        'code-block-wrapper border-[2px] rounded-xs w-full max-w-full',
        compact || wrapCode ? 'my-0' : 'my-sm',
        !compact && !wrapCode && 'code-block-roomy @container',
        'relative min-w-0 overflow-hidden',
        setupForDark('border-secondary')
      )}
    >
      <CopyButton
        text={decodedCode}
        ariaLabel='Copy code to clipboard'
        show={showCopyButton !== false}
      />
      <pre className={preClass}>
        <code
          className={`hljs language-${resolvedLanguage} block${wrapCode ? ' code-wrap' : ''}`}
          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
        />
      </pre>
    </div>
  );
}

function KatexBlock({ latex }: { latex: string }) {
  const { html, error } = useMemo(() => {
    try {
      return {
        html: katex.renderToString(latex, { throwOnError: true, displayMode: true }),
        error: null as string | null
      };
    } catch (err) {
      return {
        html: null as string | null,
        error: err instanceof Error ? err.message : String(err)
      };
    }
  }, [latex]);

  if (error || !html) {
    return (
      <div className='my-sm flex flex-col gap-xs'>
        <pre
          className={classNames(
            'hljs P6R sm:P2R border-[2px] rounded-xs p-xs whitespace-pre-wrap break-words',
            setupForDark('border-secondary')
          )}
        >
          {latex}
        </pre>
        <div
          role='alert'
          className='P6R sm:P4R border border-error text-error rounded-xs p-xs whitespace-pre-wrap break-words'
        >
          {`KaTeX error: ${error}`}
        </div>
      </div>
    );
  }

  return (
    <div className='katex-block-wrapper relative my-md mx-xxs'>
      <div dangerouslySetInnerHTML={{ __html: html }} />
      <CopyButton text={latex} ariaLabel='Copy LaTeX to clipboard' />
    </div>
  );
}

interface MarkdownTextProps extends React.HTMLAttributes<HTMLDivElement> {
  text: string;
  showCopyButton?: boolean;
  compact?: boolean;
  wrapped?: boolean;
  selectable?: boolean;
}

export default function MarkdownText({
  text,
  showCopyButton = true,
  compact = false,
  wrapped = false,
  selectable = true,
  className = '',
  ...props
}: MarkdownTextProps) {
  const isCompact = compact;
  const isCompactWrapped = compact && wrapped;
  const isWrapped = wrapped;
  const codeBlocksRef = useRef<Array<{ id: string; code: string; language?: string }>>([]);
  const katexBlocksRef = useRef<Array<{ id: string; latex: string }>>([]);
  const [renderedBlocks, setRenderedBlocks] = useState<React.JSX.Element[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  const marked = useMemo(() => {
    const markedInstance = new Marked();
    const renderer = new markedInstance.Renderer();

    // ALLOWED
    // renderer.strong = function(text: string): string {};
    // renderer.em = function(text: string): string {};
    // renderer.list = function(body: string , boolean ordered, number start): string {}
    // renderer.listitem = function(text: string , boolean task, boolean checked): string {}
    // renderer.html = function(html: string, block: boolean): string {}
    // renderer.hr = function(): string {}
    // renderer.br = function(): string {}

    // CUSTOMIZED
    renderer.link = function (href: string, title: string, text: string): string {
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a class="text-additional dark:text-additional-dark" href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
    };

    renderer.heading = function (text: string, level: number, raw: string): string {
      if (isCompact) {
        return `<div class="sm:H6 H6 py-xxs">${text}</div>`;
      }

      // not compact
      if (level === 1) {
        return `<div class="sm:H3 H6 sm:py-md py-xs px-xs">${text}</div>`;
      }
      if (level >= 2) {
        return `<div class="sm:H5 H6 sm:py-md py-xs px-xs">${text}</div>`;
      }
    };

    renderer.paragraph = function (text: string): string {
      if (isCompact) {
        return `<div class="sm:P4R P6R !leading-[1.3]">${text}</div>`;
      }
      return `<div class="sm:P2R P6R !leading-[1.5]">${text}</div>`;
    };

    renderer.code = function (code: string, infostring = ''): string {
      const language = infostring.trim().split(/\s+/)[0] || '';
      const id = `code-block-${Math.random().toString(36).substring(2, 11)}`;
      codeBlocksRef.current.push({
        id,
        code,
        language
      });

      return `<div key="${id}" id="${id}" class="sm:P2R P4R code-block-placeholder w-full min-w-0"></div>`;
    };

    renderer.codespan = function (code: string): string {
      const sizeClass = isCompact ? 'P7R' : 'sm:P2R P4R';
      const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<code class="${sizeClass} bg-gray-900 text-black px-xxs py-[2px] rounded-xxs">${escaped}</code>`;
    };

    // TABLES
    renderer.table = function (header: string, body: string): string {
      if (isCompact) {
        const tableLayoutClass = isCompactWrapped ? ' max-w-full' : '';
        return `<div class="table-wrapper w-full overflow-x-auto my-xxs">
        <table class="markdown-table border-collapse${tableLayoutClass}">
          <thead>${header}</thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
      }
      const tableLayoutClass = isWrapped ? ' max-w-full' : ' min-w-full';
      const tableMargin = isWrapped ? 'my-0' : 'my-sm';
      return `<div class="table-wrapper w-full overflow-x-auto ${tableMargin}">
      <table class="markdown-table border-collapse border-2 border-secondary${tableLayoutClass}">
        <thead>${header}</thead>
        <tbody>${body}</tbody>
      </table>
    </div>`;
    };

    renderer.tablerow = function (content: string): string {
      return `<tr>${content}</tr>`;
    };

    renderer.tablecell = function (content: string, flags: any): string {
      const tag = flags.header ? 'th' : 'td';
      let alignClass = 'text-left';
      if (flags.align === 'center') alignClass = 'text-center';
      if (flags.align === 'right') alignClass = 'text-right';
      if (isCompact) {
        const wrapClass = isCompactWrapped
          ? 'break-words overflow-wrap-anywhere'
          : 'whitespace-nowrap';
        const headerClass = flags.header ? `P7M bg-secondary ${wrapClass}` : `P7R ${wrapClass}`;
        return `<${tag} class="${setupForDark('px-xs py-[2px] border border-primary')} ${alignClass} ${headerClass}">${content}</${tag}>`;
      }
      const wrapClass = isWrapped ? 'break-words overflow-wrap-anywhere' : 'break-words';
      const headerClass = flags.header
        ? `sm:P2B P6B bg-secondary ${wrapClass}`
        : `sm:P2R P6R ${wrapClass}`;
      return `<${tag} class="${setupForDark('px-sm py-xs border border-primary')} ${alignClass} ${headerClass}">${content}</${tag}>`;
    };

    // DISALLOWED
    renderer.blockquote = function (quote: string): string {
      return quote;
    };

    renderer.checkbox = function (checked: boolean): string {
      return checked ? '[x]' : '[ ]';
    };

    renderer.del = function (text: string): string {
      return text;
    };

    renderer.image = function (href: string, title: string, text: string): string {
      return href;
    };

    renderer.text = function (text: string): string {
      return text;
    };

    renderer.hr = function (): string {
      return '';
    };

    renderer.list = function (body: string, ordered: boolean): string {
      const listType = ordered ? 'list-decimal' : 'list-disc';
      const listTag = ordered ? 'ol' : 'ul';
      if (isCompact) {
        return `<${listTag} class="P6R ${listType} pl-sm py-xxs">${body}</${listTag}>`;
      }
      return `<${listTag} class="sm:P2R P4R ${listType} pl-md py-sm">${body}</${listTag}>`;
    };

    renderer.listitem = function (text: string, task: boolean, checked: boolean): string {
      if (isCompact) {
        return `<li class="P6R mb-[2px]">${text}</li>`;
      }
      return `<li class="sm:P2R P4R mb-xs">${text}</li>`;
    };

    const legacyHighlightExtension = {
      name: 'legacyHighlight',
      level: 'inline',
      start(src) {
        return src.match(/=/)?.index;
      },
      tokenizer(src, tokens) {
        const rule = /^=([^=]+)=/;
        const match = rule.exec(src);
        if (match) {
          return {
            type: 'legacyHighlight',
            raw: match[0],
            text: match[1].trim(),
            tokens: this.lexer.inlineTokens(match[1].trim())
          };
        }
      },
      renderer(token) {
        return `<span class="sm:P2R P4R bg-yellow-300 px-xxs py-[1px] rounded-xxs text-black">${this.parser.parseInline(token.tokens)}</span>`;
      },
      childTokens: ['tokens']
    };

    // Color-tag highlights:
    //   Uppercase R/B/Y/G[…] → solid background pill (text-primary on bg-{color}-500)
    //   Lowercase r/b/y/g[…] → coloured text only (text-{color}-700, no padding/bg)
    const COLOR_TAG_CLASSES: Record<string, string> = {
      R: classNames('px-xxs py-[1px] rounded-xxs', setupForDark('bg-red-500 text-primary')),
      B: classNames('px-xxs py-[1px] rounded-xxs', setupForDark('bg-blue-500 text-primary')),
      Y: classNames('px-xxs py-[1px] rounded-xxs', setupForDark('bg-yellow-300 text-primary')),
      G: classNames('px-xxs py-[1px] rounded-xxs', setupForDark('bg-green-500 text-primary')),
      r: classNames(setupForDark('text-red-300')),
      b: classNames(setupForDark('text-blue-300')),
      y: classNames(setupForDark('text-yellow-500')),
      g: classNames(setupForDark('text-green-300'))
    };

    const colorTagExtension = {
      name: 'colorTag',
      level: 'inline',
      start(src) {
        return src.match(/[RBYGrbyg]\[/)?.index;
      },
      tokenizer(src) {
        const match = /^([RBYGrbyg])\[([^\]]+)\]/.exec(src);
        if (match) {
          return {
            type: 'colorTag',
            raw: match[0],
            marker: match[1],
            text: match[2],
            tokens: this.lexer.inlineTokens(match[2])
          };
        }
      },
      renderer(token) {
        const cls = COLOR_TAG_CLASSES[token.marker] || '';
        return `<span class="${classNames(cls)}">${this.parser.parseInline(token.tokens)}</span>`;
      },
      childTokens: ['tokens']
    };

    const katexInlineExtension = {
      name: 'katexInline',
      level: 'inline',
      start(src) {
        const i = src.indexOf('$');
        return i === -1 ? undefined : i;
      },
      tokenizer(src) {
        // Inline math: `$<latex>$` (no leading/trailing whitespace, no nested $$)
        const KATEX_INLINE_REGEX = /^\$(?!\$)([^\n$]+?)\$/;
        const match = KATEX_INLINE_REGEX.exec(src);
        if (match && match[1].trim().length > 0) {
          return { type: 'katexInline', raw: match[0], text: match[1].trim() };
        }
      },
      renderer(token) {
        try {
          return katex.renderToString(token.text, {
            throwOnError: false,
            errorColor: '#e04e4e',
            displayMode: false
          });
        } catch {
          return `<span class="katex-error">${token.text}</span>`;
        }
      }
    };

    const katexBlockExtension = {
      name: 'katexBlock',
      level: 'block',
      start(src) {
        const i = src.indexOf('$$');
        return i === -1 ? undefined : i;
      },
      tokenizer(src) {
        // Display math: `$$<latex>$$` (multi-line allowed)
        const KATEX_BLOCK_REGEX = /^\$\$([\s\S]+?)\$\$/;
        const match = KATEX_BLOCK_REGEX.exec(src);
        if (match && match[1].trim().length > 0) {
          return { type: 'katexBlock', raw: match[0], text: match[1].trim() };
        }
      },
      renderer(token) {
        const id = `katex-block-${Math.random().toString(36).substring(2, 11)}`;
        katexBlocksRef.current.push({ id, latex: token.text });
        return `<div id="${id}" class="katex-block-placeholder"></div>`;
      }
    };

    markedInstance.use({
      pedantic: false,
      gfm: true,
      breaks: true,
      renderer: renderer,
      extensions: [
        legacyHighlightExtension,
        colorTagExtension,
        katexBlockExtension,
        katexInlineExtension
      ]
    });

    return markedInstance;
  }, [isCompact, isCompactWrapped, isWrapped]);

  // Effect to handle markdown parsing and code block rendering
  useEffect(() => {
    if (!contentRef.current) return;

    // Reset code & katex block arrays
    codeBlocksRef.current = [];
    katexBlocksRef.current = [];

    // Parse markdown to HTML and sanitize to prevent XSS
    const parsedHtml = marked.parse(text || '') as string;
    const sanitizedHtml = DOMPurify.sanitize(parsedHtml, {
      ADD_ATTR: ['target']
    });

    // Set the HTML content
    contentRef.current.innerHTML = sanitizedHtml;

    // After DOM update, render React components into placeholders
    const blocks: React.JSX.Element[] = [];

    codeBlocksRef.current.forEach(({ id, code, language }) => {
      const placeholder = contentRef.current?.querySelector(`#${id}`);

      if (placeholder) {
        const isMermaid = language?.trim().toLowerCase() === 'mermaid';
        blocks.push(
          <React.Fragment key={id}>
            {createPortal(
              isMermaid ? (
                <MermaidBlock code={code} />
              ) : (
                <CodeBlock
                  code={code}
                  language={language}
                  showCopyButton={showCopyButton}
                  compact={isCompact}
                  wrapCode={isWrapped}
                />
              ),
              placeholder as HTMLElement
            )}
          </React.Fragment>
        );
      }
    });

    katexBlocksRef.current.forEach(({ id, latex }) => {
      const placeholder = contentRef.current?.querySelector(`#${id}`);
      if (placeholder) {
        blocks.push(
          <React.Fragment key={id}>
            {createPortal(<KatexBlock latex={latex} />, placeholder as HTMLElement)}
          </React.Fragment>
        );
      }
    });

    setRenderedBlocks(blocks);
  }, [text, showCopyButton, marked]);

  return (
    <>
      <style>{`
        .code-block-placeholder {
          width: 100%;
          min-width: 0;
        }
        .code-block-wrapper {
          max-width: 100%;
          min-width: 0;
        }
        .code-block-wrapper pre {
          margin: 0;
          padding: 0;
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          min-width: 0;
        }
        .code-block-wrapper code {
          white-space: pre;
          display: block;
          min-width: 0;
        }
        .code-block-wrapper code.code-wrap {
          white-space: pre-wrap;
          word-break: break-word;
        }
        .code-block-wrapper.code-block-roomy pre code {
          padding: 0.5em;
        }
        @container (min-width: 40rem) {
          .code-block-wrapper.code-block-roomy pre code {
            padding: 0.85em;
          }
        }
        .overflow-wrap-anywhere {
          overflow-wrap: anywhere;
        }
        .katex {
          font-size: 1.25em;
        }
        .katex-display {
          font-size: 1.6em;
          background-color: #151515;
          border: 2px solid #3a3a3c;
          border-radius: 8px;
          padding: 8px;
          margin: 0;
          overflow-x: auto;
          overflow-y: hidden;
        }
        .dark .katex-display {
          background-color: #ffffff;
          border-color: #d1d1d6;
        }
        .katex-display > .katex {
          font-size: inherit;
          white-space: nowrap;
        }
        .katex-error {
          display: inline-block;
          border: 1px solid #e04e4e;
          border-radius: 4px;
          padding: 2px 6px;
          font-family: ui-monospace, Menlo, Consolas, monospace;
          font-size: 0.9em;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .table-wrapper {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
      
        .markdown-table {
          border-collapse: collapse;
          width: 100%;
          min-width: 0;
        }
        
        .markdown-table th {
          font-weight: 600;
          background-color: var(--secondary, #1f2937);
        }
        
        .markdown-table td,
        .markdown-table th {
          border: 1px solid var(--border-secondary, #e5e7eb);
          padding: 0.5rem 0.75rem;
        }
        
        .markdown-table tr:first-child th,
        .markdown-table tr:first-child td {
          border-top: none;
        }
        .markdown-table tr:last-child td {
          border-bottom: none;
        }
        .markdown-table td:first-child,
        .markdown-table th:first-child {
          border-left: none;
        }
        .markdown-table td:last-child,
        .markdown-table th:last-child {
          border-right: none;
        }
        .markdown-table tr:hover {
          background-color: rgba(0, 0, 0, 0.02);
        }
      `}</style>
      <div
        ref={contentRef}
        className={classNames(selectable ? 'select-text' : 'select-none', className)}
        {...props}
      />
      {renderedBlocks}
    </>
  );
}
